/**
 * OfflineBanner — muestra el estado de conexion y sincronizacion.
 * Se coloca en el layout principal (App.js o Layout).
 */
import { useState, useEffect, useCallback } from "react";
import { Wifi, WifiOff, RotateCw, Check, Loader2 } from "lucide-react";
import { processQueue, countPending, scheduleBackgroundSync } from "@/lib/offlineQueue";
import { useAuth } from "@/lib/auth";

export function OfflineBanner() {
  const { token } = useAuth();
  const [online, setOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);

  const refreshCount = useCallback(async () => {
    const n = await countPending();
    setPendingCount(n);
  }, []);

  useEffect(() => {
    refreshCount();
    const handleOnline = () => {
      setOnline(true);
      refreshCount().then(() => {
        // Al volver online, intentar sync automático
        handleSync();
      });
    };
    const handleOffline = () => setOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Escuchar mensajes del SW (sync completado)
    const handleSWMessage = (e) => {
      if (e.data?.chizy_outbox_processed) {
        refreshCount();
        setJustSynced(true);
        setTimeout(() => setJustSynced(false), 4000);
      }
    };
    navigator.serviceWorker?.addEventListener("message", handleSWMessage);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      navigator.serviceWorker?.removeEventListener("message", handleSWMessage);
    };
  }, [refreshCount]);

  const handleSync = useCallback(async () => {
    if (!online || syncing) return;
    setSyncing(true);
    try {
      // Intentar Background Sync primero
      const bgSyncOk = await scheduleBackgroundSync();
      if (!bgSyncOk) {
        // Fallback: procesar directamente
        const { processed } = await processQueue(token);
        if (processed > 0) {
          setJustSynced(true);
          setTimeout(() => setJustSynced(false), 4000);
        }
      }
      await refreshCount();
    } catch (e) {
      console.warn("[offline-banner] sync error", e);
    } finally {
      setSyncing(false);
    }
  }, [online, syncing, token, refreshCount]);

  // No mostrar nada si online y sin pendientes
  if (online && pendingCount === 0 && !justSynced) return null;

  if (!online) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm shadow-lg">
        <WifiOff className="w-4 h-4 shrink-0 text-amber-400" />
        <span className="font-medium">Sin conexion</span>
        {pendingCount > 0 && (
          <span className="text-slate-400 ml-1">— {pendingCount} {pendingCount === 1 ? "cambio pendiente" : "cambios pendientes"}</span>
        )}
      </div>
    );
  }

  if (syncing) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm shadow-lg">
        <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
        <span>Conexion restaurada. Sincronizando {pendingCount} {pendingCount === 1 ? "cambio" : "cambios"} pendientes...</span>
      </div>
    );
  }

  if (justSynced && pendingCount === 0) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm shadow-lg">
        <Check className="w-4 h-4 shrink-0" />
        <span>Listo, todo sincronizado.</span>
      </div>
    );
  }

  if (pendingCount > 0 && online) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-between gap-2 px-4 py-2.5 bg-amber-600 text-white text-sm shadow-lg">
        <div className="flex items-center gap-2">
          <Wifi className="w-4 h-4 shrink-0" />
          <span>{pendingCount} {pendingCount === 1 ? "cambio" : "cambios"} sin sincronizar</span>
        </div>
        <button
          onClick={handleSync}
          className="flex items-center gap-1 text-xs font-semibold bg-white/20 hover:bg-white/30 px-2.5 py-1 rounded-full transition-colors"
          data-testid="sync-now-btn"
        >
          <RotateCw className="w-3 h-3" />
          Sincronizar
        </button>
      </div>
    );
  }

  return null;
}

/**
 * InstallPWABanner — banner para instalar la PWA en Chrome/Safari.
 * Solo se muestra a operativos y branch managers.
 */
export function InstallPWABanner() {
  const { user } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem("chizy-pwa-dismissed") === "1";
  });
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    const isIOSDevice = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    setIsIOS(isIOSDevice && !isStandalone);

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
  }, []);

  if (!user) return null;
  if (!["branch_manager", "operativo"].includes(user.role)) return null;
  if (dismissed) return null;
  if (window.matchMedia("(display-mode: standalone)").matches) return null;
  if (!deferredPrompt && !isIOS) return null;

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setDeferredPrompt(null);
      localStorage.setItem("chizy-pwa-dismissed", "1");
      setDismissed(true);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem("chizy-pwa-dismissed", "1");
    setDismissed(true);
  };

  return (
    <div className="mx-4 mt-3 mb-1 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3 text-sm">
      <div className="flex-1">
        <p className="font-semibold text-amber-900">Instala Chizy Ops en tu dispositivo</p>
        <p className="text-xs text-amber-700 mt-0.5">
          Funciona sin internet. Los checklists se sincronizan automaticamente al reconectarte.
        </p>
        {isIOS && showIOSInstructions && (
          <p className="text-xs text-amber-800 mt-2 bg-amber-100 rounded p-2">
            En Safari: toca el boton compartir y despues "Agregar a la pantalla de inicio".
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {isIOS ? (
          <button
            onClick={() => setShowIOSInstructions(p => !p)}
            className="text-xs font-semibold text-amber-800 bg-amber-200 hover:bg-amber-300 px-3 py-1.5 rounded-lg transition-colors"
            data-testid="ios-install-instructions-btn"
          >
            Como instalar
          </button>
        ) : (
          <button
            onClick={handleInstall}
            className="text-xs font-semibold text-amber-900 bg-[#FFDF00] hover:bg-yellow-300 px-3 py-1.5 rounded-lg transition-colors"
            data-testid="install-pwa-btn"
          >
            Instalar
          </button>
        )}
        <button onClick={handleDismiss} className="text-amber-400 hover:text-amber-600 text-lg leading-none font-light">
          ×
        </button>
      </div>
    </div>
  );
}
